import React, { useCallback } from 'react';
import { createPortal as reactCreatePortal } from 'react-dom';

import { useElementRef } from './useElementRef';

export type CreatePortalFn = (children: React.ReactNode, key?: string | null) => React.ReactPortal | null;

export interface UsePortalReturnType {
  /**
   * A function (analogous to ReactDOM.createPortal) that renders the given content in the
   * associated portal.
   */
  createPortal: CreatePortalFn;
  /**
   * A React element used to render the portal's contents. Render this where the portal's contents
   * should appear in the component tree.
   */
  portal: JSX.Element;
}

/**
 * Returns a function to create a portal and a React element to be used to render the portal's
 * contents.
 */
export const usePortal = (): UsePortalReturnType => {
  const { element, ref } = useElementRef<HTMLDivElement>();

  const portal = <div ref={ref} />;

  const createPortal = useCallback<CreatePortalFn>(
    (children, key) => {
      if (!element) {
        return null;
      }
      return reactCreatePortal(children, element, key);
    },
    [element],
  );

  return { createPortal, portal };
};
